import { useRecommendations } from './recommendations';
import { useSettings } from '@/store/settings';
import { LoanOption, Loan, PaymentBand, LoanType, MortgageOption } from '@/types';
import { defineStore, storeToRefs } from 'pinia';
import { BerwowEvent, TrackingEvent, emit } from '@/helpers';
import { DEFAULT_MAX_TERM } from '@/types/constants';

interface LoanState {
  loanOptions: LoanOption[],
  selectedLoanOption: LoanOption | undefined;
  totalAmount: number;
  maxAmount: number;
  loanAmount: number;
  term: number | undefined;
  paymentBandStatic: PaymentBand | undefined;
  mortgageAmountValue: number;
  mortgageTerm: number;
  mortgageActive: boolean | undefined;
  displayLoan: boolean | undefined;
  displayMortgage: boolean | undefined;
}

export const useLoan = defineStore('loan', {
  state: (): LoanState => ({
    loanOptions: [],
    selectedLoanOption: undefined,
    totalAmount: -1,
    maxAmount: -1,
    loanAmount: 0,
    term: undefined,
    paymentBandStatic: undefined,
    mortgageAmountValue: 0,
    mortgageActive: false,
    mortgageTerm: 1,
    displayLoan: undefined,
    displayMortgage: undefined,
  }),
  getters: {
    paymentBand(): PaymentBand | undefined {
      return this.selectedLoanOption?.paymentBands?.find(band => this.loanAmount >= band.minAmount && band.maxAmount >= this.loanAmount);
    },
    selectedLoan(): Loan | undefined {
      return this.selectedLoanOption ? {
        name: this.selectedLoanOption.name,
        paymentBands: this.selectedLoanOption.paymentBands,
        term: this.term ? this.term : 0,
        amount: this.loanAmount != undefined ? this.loanAmount : 0,
      } : undefined;
    },
    availableLoanOpts(): LoanOption[] {
      return this.loanOptions.filter((opt) =>
        opt.paymentBands === undefined || opt.paymentBands?.find(band =>
          this.totalAmount >= band.minAmount && this.totalAmount < band.maxAmount
        ) !== undefined || this.totalAmount >= opt.paymentBands[opt.paymentBands.length - 1].maxAmount
      );
    },
    mortgage(): MortgageOption {
      return {
        yearsRemaining: this.mortgageTerm,
        outstandingAmount: this.mortgageAmountValue
      };
    }
  },
  actions: {
    async addLoanOptions(loanOptions: any[], firstRequest = false) {
      const { settings } = useSettings();
      this.loanOptions = [...loanOptions];
      const noLoan = { name: 'No loan' };
      this.loanOptions.unshift(noLoan);

      if (settings && settings.defaultLoanOption != undefined) {
        this.selectedLoanOption = this.loanOptions[settings.defaultLoanOption + 1];
        this.setTerm();
        if (!this.paymentBand && !firstRequest) {
          await this.setSelectedLoanOption(loanOptions[0]);
        }
      }
    },
    async setSelectedLoanOption(loan: LoanOption, updateLoanAmount?: boolean, byId?: boolean) {
      if (loan) {
        this.selectedLoanOption = loan;
        if (loan.name == 'No loan') {
          this.term = 0;
          this.loanAmount = 0;
        } else {
          this.term = loan.paymentBands && loan.paymentBands[0] ? loan.paymentBands[0].minTerm : 1;
          this.loanAmount = loan.paymentBands && loan.paymentBands[0] ? loan.paymentBands[0].minAmount : 1;
          await this.updateSelectedLoan();
          const { settings } = storeToRefs(useSettings());
          this.term = loan.defaultTerm ? loan.defaultTerm : this.paymentBand ? this.paymentBand?.maxTerm
            : settings.value?.defaultTerm ? settings.value.defaultTerm.max : DEFAULT_MAX_TERM;
        }
        if (!byId) await this.updateSelectedLoan(updateLoanAmount);
      }
    },
    async setSelectedLoanOptionById(term: number, loanAmount: number) {
      this.term = term;
      this.loanAmount = loanAmount
      await this.updateSelectedLoan(false);
    },
    async updateSelectedLoan(updateLoanAmount = true) {
      if (this.selectedLoanOption) {
        await useRecommendations().applyRecommendations(updateLoanAmount);
      }
    },
    async addLoanDataById(loanOpts: Array<string | LoanOption>, selected: any) {
      await this.addLoanOptions(loanOpts, true);
      const opt = this.loanOptions.find(opt => opt.name === selected.name);
      if (opt) {
        const amount = useSettings().hasCostsAndSavings ? selected.amount : Math.floor(selected.amount / 1000) * 1000;
        await this.setSelectedLoanOption(opt, false, true);
        if (selected.term != this.term || this.loanAmount != amount) await this.setSelectedLoanOptionById(
            selected.term,
            amount
        );
      }
    },
    async setAllLoanAmounts(amount: number, forceTermUpdate?: boolean) {
      this.loanAmount = useSettings().hasCostsAndSavings ? amount : Math.floor(amount / 1000) * 1000;
      this.maxAmount = amount;
      this.totalAmount = this.loanAmount;

      if (this.selectedLoanOption && this.selectedLoanOption.paymentBands) {
        const maxAmountBands = Math.max(...this.selectedLoanOption.paymentBands.map(band => band.maxAmount));
        if (this.loanAmount > maxAmountBands) this.loanAmount = Math.floor(maxAmountBands / 1000) * 1000;
        if (this.totalAmount > maxAmountBands) this.totalAmount = Math.floor(maxAmountBands / 1000) * 1000;
      }

      if (!this.term || forceTermUpdate) {
        this.setTerm();
      } else {
        this.checkPaymentBandTerm();
      }

      if (this.selectedLoan && this.selectedLoan?.name !== 'No loan' &&
        (this.selectedLoanOption?.loanType !== LoanType.Mortgage && this.paymentBand == undefined) ||
        (this.selectedLoanOption?.loanType === LoanType.Mortgage && this.totalAmount === 0)
      ) {
        await this.setSelectedLoanOption(this.loanOptions[0]); //No loan
        emit(BerwowEvent.UnavailableAmount, {});
      }

      await useRecommendations().applyRecommendations(false);
    },
    setTerm() {
      this.term =
        this.selectedLoanOption && this.selectedLoanOption.defaultTerm &&
          (this.paymentBand && //default term exists
            (this.selectedLoanOption.defaultTerm <= this.paymentBand.maxTerm && this.selectedLoanOption.defaultTerm >= this.paymentBand.minTerm) //works for payment band
            || this.selectedLoanOption.loanType === LoanType.Mortgage)
          ? this.selectedLoanOption.defaultTerm
          : this.paymentBand ? this.paymentBand.maxTerm : 1;
    },
    checkPaymentBandTerm() {
      const updateAmountSlider = this.paymentBandStatic != this.paymentBand;
      if (updateAmountSlider) {
        if (this.paymentBandStatic && this.paymentBand && this.paymentBandStatic.maxTerm != this.paymentBand.maxTerm) {
          this.setTerm();
        }
        this.paymentBandStatic = this.paymentBand;

      } else if (this.paymentBand && this.term && this.paymentBand.maxTerm < this.term) {
        this.setTerm();
      }
    },
    async loanAmountChanged() {
      this.checkPaymentBandTerm();
      await this.updateSelectedLoan(false);
      useRecommendations().emitRecommendationsReportResult(
        false,
        TrackingEvent.UserChangedLoan
      );
    }
  },
});
